/**
 * Integrytas Group - Base App (MOBILE)
 * Page: PHE.HOOKS — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/PheHooksPage.tsx (the Pipeline Hook
 * Engine). A DISTINCT presentation (KPI header + dense card list + facet chips +
 * status pills + bottom-sheet detail + edit sheet) over the SAME data/logic layer
 * only:
 *   - reads via apiDb (phe_hook, plus a phe_hook_log roll-up matched by
 *     internal_code for the per-hook recent-run stage/status, and a
 *     phe_function_whitelist count for the KPI header),
 *   - the activate/deactivate + scalar edits (name, phase, operation, action type,
 *     error policy, priority, active) write through the Common Routine
 *     (routedUpdate) exactly like the desktop page — §2 single write path; never a
 *     direct apiDb write.
 *
 * phe_hook is a technical config table: internal_code / entity_name /
 * workspace_code are NATURAL identifiers (a hook code, the table it fires on, and
 * the portal workspace it is scoped to) shown as their stored technical codes,
 * exactly as the desktop page does — there is no _tl lookup to resolve (same as
 * MobileMtaRuleManagerPage). phase / operation / action_type / on_error / (log)
 * outcome are CHECK enums, so every displayed enum label goes through i18n
 * labelled constants, never a bare stored literal (§17.B11). Y/N renders as
 * status pills (§17.B10).
 *
 * Authoring the conditions / action_config JSON stays on the desktop (summarised
 * read-only here, mirroring MobileMtaRuleEditSheet). §16: never the desktop grid
 * re-hosted.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Search, Inbox, Power, X, SquarePen, Webhook } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedUpdate } from '../../lib/routed_writes';
import { useFormatDateTime } from '../../lib/format_date';
import { MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill, type MobilePillTone } from '../components';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'PHE.HOOKS';

type Phase = 'BEFORE' | 'INSTEAD' | 'AFTER';
type Operation = 'ANY' | 'ADD' | 'CHANGE' | 'DELETE';
type ActionType = 'TRANSFORM' | 'ABORT' | 'CALL_FUNCTION' | 'WEBHOOK' | 'NOTIFY';
type OnError = 'FAIL_OPEN' | 'FAIL_CLOSED';

interface MobileLogRow {
  pk: number;
  outcome: string | null;
  message: string | null;
  at: string;
}

interface MobileHookRow {
  pk: number;
  internal_code: string;
  name: string;
  workspace_code: string;
  entity_name: string;
  phase: Phase;
  operation: Operation;
  conditions: unknown;
  action_type: ActionType;
  action_config: unknown;
  on_error: OnError;
  priority: number;
  is_active: 'Y' | 'N';
  runs: MobileLogRow[];
}

type StatusFacet = 'all' | 'active' | 'inactive';
type PhaseFacet = 'all' | Phase;

// §17.B11: CHECK-enum labels via i18n labelled constants — never a bare literal.
const PHASE_OPTIONS: { value: Phase; key: string; fallback: string }[] = [
  { value: 'BEFORE', key: 'mobile.phe.phase.BEFORE', fallback: 'Before' },
  { value: 'INSTEAD', key: 'mobile.phe.phase.INSTEAD', fallback: 'Instead of' },
  { value: 'AFTER', key: 'mobile.phe.phase.AFTER', fallback: 'After' },
];

const OPERATION_OPTIONS: { value: Operation; key: string; fallback: string }[] = [
  { value: 'ANY', key: 'mobile.phe.operation.ANY', fallback: 'Any write' },
  { value: 'ADD', key: 'mobile.phe.operation.ADD', fallback: 'Add' },
  { value: 'CHANGE', key: 'mobile.phe.operation.CHANGE', fallback: 'Change' },
  { value: 'DELETE', key: 'mobile.phe.operation.DELETE', fallback: 'Delete' },
];

const ACTION_TYPE_OPTIONS: { value: ActionType; key: string; fallback: string }[] = [
  { value: 'TRANSFORM', key: 'mobile.phe.action.TRANSFORM', fallback: 'Transform' },
  { value: 'ABORT', key: 'mobile.phe.action.ABORT', fallback: 'Abort' },
  { value: 'CALL_FUNCTION', key: 'mobile.phe.action.CALL_FUNCTION', fallback: 'Call function' },
  { value: 'WEBHOOK', key: 'mobile.phe.action.WEBHOOK', fallback: 'Webhook' },
  { value: 'NOTIFY', key: 'mobile.phe.action.NOTIFY', fallback: 'Notify' },
];

const ON_ERROR_OPTIONS: { value: OnError; key: string; fallback: string }[] = [
  { value: 'FAIL_OPEN', key: 'mobile.phe.on_error.FAIL_OPEN', fallback: 'Fail open' },
  { value: 'FAIL_CLOSED', key: 'mobile.phe.on_error.FAIL_CLOSED', fallback: 'Fail closed' },
];

// Run-log outcome codes (CHECK enum) → localized label + status-pill tone.
const OUTCOME_META: Record<string, { key: string; fallback: string; tone: MobilePillTone }> = {
  OK: { key: 'mobile.phe.outcome.OK', fallback: 'OK', tone: 'ok' },
  SKIPPED: { key: 'mobile.phe.outcome.SKIPPED', fallback: 'Skipped', tone: 'info' },
  ABORTED: { key: 'mobile.phe.outcome.ABORTED', fallback: 'Aborted', tone: 'no' },
  OVERRODE: { key: 'mobile.phe.outcome.OVERRODE', fallback: 'Overrode', tone: 'wait' },
  ERROR: { key: 'mobile.phe.outcome.ERROR', fallback: 'Error', tone: 'no' },
};

function optionLabel(
  t: TFunction,
  options: { value: string; key: string; fallback: string }[],
  value: string,
): string {
  const opt = options.find((o) => o.value === value);
  return opt ? t(opt.key, opt.fallback) : value;
}

function outcomeLabel(t: TFunction, outcome: string | null): string {
  if (!outcome) return t('mobile.phe.outcome.unknown', 'Unknown');
  const meta = OUTCOME_META[outcome];
  return meta ? t(meta.key, meta.fallback) : outcome;
}

function outcomeTone(outcome: string | null): MobilePillTone {
  const meta = outcome ? OUTCOME_META[outcome] : undefined;
  return meta ? meta.tone : 'info';
}

/** Compact, human summary of the conditions JSON blob (no raw JSON on a phone). */
function conditionsSummary(t: TFunction, conditions: unknown): string {
  if (conditions && typeof conditions === 'object') {
    const c = conditions as { match?: unknown; conditions?: unknown };
    const list = Array.isArray(c.conditions) ? c.conditions : [];
    if (list.length > 0) {
      const matchRaw = typeof c.match === 'string' ? c.match.toLowerCase() : 'all';
      const matchLabel =
        matchRaw === 'any'
          ? t('mobile.phe.match_any', 'match any')
          : t('mobile.phe.match_all', 'match all');
      return `${matchLabel} · ${t('mobile.phe.conditions_count', {
        count: list.length,
        defaultValue: `${list.length} condition(s)`,
      })}`;
    }
  }
  return t('mobile.phe.conditions_none', 'No conditions (always runs)');
}

/** Compact, human summary of action_config, keyed off the action type. */
function actionConfigSummary(t: TFunction, actionType: ActionType, cfg: unknown): string {
  const none = t('mobile.phe.config_none', 'Not configured');
  if (!cfg || typeof cfg !== 'object') return none;
  const c = cfg as Record<string, unknown>;
  switch (actionType) {
    case 'CALL_FUNCTION':
      return typeof c.function === 'string' ? c.function : none;
    case 'ABORT':
      return typeof c.message === 'string' ? c.message : none;
    case 'NOTIFY':
      return typeof c.category === 'string' ? c.category : none;
    case 'WEBHOOK':
      return typeof c.url === 'string' ? c.url : none;
    case 'TRANSFORM': {
      const count = c.set && typeof c.set === 'object' ? Object.keys(c.set as object).length : 0;
      return t('mobile.phe.transform_fields', { count, defaultValue: `${count} field(s) set` });
    }
    default:
      return none;
  }
}

export function MobilePheHooksPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const fmtDateTime = useFormatDateTime();
  const [hooks, setHooks] = useState<MobileHookRow[]>([]);
  const [fnActiveCount, setFnActiveCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [phaseFacet, setPhaseFacet] = useState<PhaseFacet>('all');
  const [selected, setSelected] = useState<MobileHookRow | null>(null);
  const [editing, setEditing] = useState<MobileHookRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const [h, f, l] = await Promise.all([
        apiDb
          .from('phe_hook')
          .select(
            'phe_hook_id, internal_code, name, workspace_code, entity_name, phase, operation, conditions, action_type, action_config, on_error, priority, is_active',
          )
          .order('phase')
          .order('priority'),
        apiDb.from('phe_function_whitelist').select('phe_function_whitelist_id, is_active'),
        apiDb
          .from('phe_hook_log')
          .select('phe_hook_log_id, internal_code, outcome, message, at')
          .order('at', { ascending: false })
          .limit(80),
      ]);
      if (h.error) throw h.error;
      if (f.error) throw f.error;
      if (l.error) throw l.error;

      // Roll up recent runs per hook (matched by internal_code) — a scannable,
      // mobile-friendly view of the desktop run-log stage/status.
      type RawLog = {
        phe_hook_log_id: number;
        internal_code: string | null;
        outcome: string | null;
        message: string | null;
        at: string;
      };
      const runsByCode: Record<string, MobileLogRow[]> = {};
      let errCount = 0;
      for (const lg of (l.data ?? []) as RawLog[]) {
        if (lg.outcome === 'ERROR' || lg.outcome === 'ABORTED') errCount += 1;
        if (!lg.internal_code) continue;
        const row: MobileLogRow = { pk: lg.phe_hook_log_id, outcome: lg.outcome, message: lg.message, at: lg.at };
        (runsByCode[lg.internal_code] ??= []).push(row);
      }

      type RawHook = {
        phe_hook_id: number;
        internal_code: string;
        name: string;
        workspace_code: string;
        entity_name: string;
        phase: Phase;
        operation: Operation;
        conditions: unknown;
        action_type: ActionType;
        action_config: unknown;
        on_error: OnError;
        priority: number;
        is_active: 'Y' | 'N';
      };
      const rows: MobileHookRow[] = ((h.data ?? []) as RawHook[]).map((r) => ({
        pk: r.phe_hook_id,
        internal_code: r.internal_code,
        name: r.name,
        workspace_code: r.workspace_code,
        entity_name: r.entity_name,
        phase: r.phase,
        operation: r.operation,
        conditions: r.conditions,
        action_type: r.action_type,
        action_config: r.action_config,
        on_error: r.on_error,
        priority: r.priority,
        is_active: r.is_active,
        runs: runsByCode[r.internal_code] ?? [],
      }));

      const fnActive = ((f.data ?? []) as { is_active: 'Y' | 'N' }[]).filter((x) => x.is_active === 'Y').length;

      setHooks(rows);
      setFnActiveCount(fnActive);
      setErrorCount(errCount);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return hooks.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (phaseFacet !== 'all' && r.phase !== phaseFacet) return false;
      if (!q) return true;
      return (
        r.internal_code.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        r.entity_name.toLowerCase().includes(q) ||
        r.workspace_code.toLowerCase().includes(q) ||
        r.phase.toLowerCase().includes(q) ||
        r.action_type.toLowerCase().includes(q)
      );
    });
  }, [hooks, filter, statusFacet, phaseFacet]);

  const activeCount = useMemo(() => hooks.filter((r) => r.is_active === 'Y').length, [hooks]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.phe.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const phaseFacets: { key: PhaseFacet; label: string }[] = [
    { key: 'all', label: t('mobile.phe.phase_all', 'All phases') },
    ...PHASE_OPTIONS.map((o) => ({ key: o.value as PhaseFacet, label: t(o.key, o.fallback) })),
  ];


  const toggleActive = async (r: MobileHookRow) => {
    setActionBusy(true);
    setError(null);
    const next = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'phe_hook', 'phe_hook_id', r.pk, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.phe.title', 'Pipeline Hooks')}
        description={t('mobile.phe.description', 'Write-path hooks, their stage, action and recent run outcomes.')}
      />

      <div className="m-kpis">
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.phe.kpi_hooks', 'Hooks')}</div>
          <div className="m-kpi-v">{hooks.length}</div>
        </div>
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.phe.kpi_active', 'Active')}</div>
          <div className="m-kpi-v">{activeCount}</div>
        </div>
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.phe.kpi_functions', 'Functions')}</div>
          <div className="m-kpi-v">{fnActiveCount}</div>
        </div>
        <div className={'m-kpi' + (errorCount > 0 ? ' m-kpi-alert' : '')}>
          <div className="m-kpi-l">{t('mobile.phe.kpi_errors', 'Recent errors')}</div>
          <div className="m-kpi-v">{errorCount}</div>
        </div>
      </div>

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.phe.search', 'Search hooks')}
          aria-label={t('mobile.phe.search', 'Search hooks')}
        />
      </div>

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      <MobileFilterChips
        chips={phaseFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => phaseFacet === v}
        onSelect={(v) => setPhaseFacet(v as PhaseFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.phe.none', 'No hooks match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.pk}
              lead={<Webhook />}
              leadTone={r.is_active === 'Y' ? 'blue' : 'slate'}
              title={r.name || r.internal_code}
              subtitle={`${r.entity_name} · ${optionLabel(t, PHASE_OPTIONS, r.phase)} · ${optionLabel(t, ACTION_TYPE_OPTIONS, r.action_type)}`}
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.phe.detail', 'Hook detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Webhook size={20} /></span>
              <span>
                <div className="m-nm">{selected.name || selected.internal_code}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>

            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.phe.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.phe.phase_lbl', 'Stage')} value={optionLabel(t, PHASE_OPTIONS, selected.phase)} />
              <MobileDetailRow label={t('mobile.phe.operation_lbl', 'Operation')} value={optionLabel(t, OPERATION_OPTIONS, selected.operation)} />
              <MobileDetailRow label={t('mobile.phe.entity', 'Entity')} value={selected.entity_name} />
              <MobileDetailRow label={t('mobile.phe.workspace', 'Workspace')} value={selected.workspace_code || t('mobile.phe.workspace_global', 'Global')} />
              <MobileDetailRow label={t('mobile.phe.action_lbl', 'Action')} value={optionLabel(t, ACTION_TYPE_OPTIONS, selected.action_type)} />
              <MobileDetailRow label={t('mobile.phe.action_config', 'Config')} value={actionConfigSummary(t, selected.action_type, selected.action_config)} />
              <MobileDetailRow label={t('mobile.phe.on_error_lbl', 'On error')} value={optionLabel(t, ON_ERROR_OPTIONS, selected.on_error)} />
              <MobileDetailRow label={t('mobile.phe.priority', 'Priority')} value={selected.priority} />
              <MobileDetailRow label={t('mobile.phe.conditions', 'Conditions')} value={conditionsSummary(t, selected.conditions)} />
            </MobileContentCard>

            {/* Recent runs — the hook's stage/status history (§17.B10 pills). */}
            <MobileContentCard>
              {selected.runs.length === 0 ? (
                <div className="m-empty">{t('mobile.phe.no_runs', 'No hook runs yet')}</div>
              ) : (
                selected.runs.slice(0, 6).map((run) => (
                  <MobileDetailRow
                    key={run.pk}
                    label={fmtDateTime(run.at)}
                    value={<MobileStatusPill tone={outcomeTone(run.outcome)}>{outcomeLabel(t, run.outcome)}</MobileStatusPill>}
                  />
                ))
              )}
            </MobileContentCard>

            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.phe.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobilePheHookEditSheet
        session={session}
        hook={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit sheet — the scalar hook fields a phone can safely edit (Name, Stage,
// Operation, Action type, Error policy, Priority, Active), saved through the
// Common Routine (routedUpdate) — §2. internal_code / entity_name /
// workspace_code and the conditions / action_config JSON stay on the desktop and
// are shown read-only here (mirrors MobileMtaRuleEditSheet).
// ---------------------------------------------------------------------------

interface MobilePheHookEditSheetProps {
  session: AuthSession;
  hook: MobileHookRow | null;
  onClose: () => void;
  onSaved: () => void;
}

function MobilePheHookEditSheet({ session, hook, onClose, onSaved }: MobilePheHookEditSheetProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [phase, setPhase] = useState<Phase>('BEFORE');
  const [operation, setOperation] = useState<Operation>('ANY');
  const [actionType, setActionType] = useState<ActionType>('TRANSFORM');
  const [onError, setOnError] = useState<OnError>('FAIL_OPEN');
  const [priorityText, setPriorityText] = useState('100');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different hook is opened.
  useEffect(() => {
    if (!hook) return;
    setName(hook.name);
    setPhase(hook.phase);
    setOperation(hook.operation);
    setActionType(hook.action_type);
    setOnError(hook.on_error);
    setPriorityText(String(hook.priority));
    setActive(hook.is_active === 'Y');
    setError(null);
  }, [hook]);

  const save = async () => {
    if (!hook) return;
    if (!name.trim()) { setError(t('mobile.phe.name_required', 'Name is required.')); return; }
    const priority = Number(priorityText);
    if (!Number.isFinite(priority) || !Number.isInteger(priority)) {
      setError(t('mobile.phe.priority_invalid', 'Priority must be a whole number.'));
      return;
    }
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'phe_hook', 'phe_hook_id', hook.pk, {
      name: name.trim(),
      phase,
      operation,
      action_type: actionType,
      on_error: onError,
      priority,
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = hook !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.phe.edit_sheet', 'Edit hook')}>
        <div className="m-grabber" />
        {hook && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Webhook size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.phe.edit_title', 'Edit hook')}</div>
                <div className="m-em">{hook.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-phe-name">{t('mobile.phe.name', 'Name')}</label>
                <input
                  id="m-phe-name"
                  className="m-inp"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="m-formrow">
                <label>{t('mobile.phe.entity', 'Entity')}</label>
                <div className="m-formrow-ro">
                  {hook.entity_name}
                  <span className="m-formrow-hint"> · {t('mobile.phe.desktop_only', 'edit on desktop')}</span>
                </div>
              </div>
              <div className="m-formrow">
                <label>{t('mobile.phe.workspace', 'Workspace')}</label>
                <div className="m-formrow-ro">
                  {hook.workspace_code || t('mobile.phe.workspace_global', 'Global')}
                  <span className="m-formrow-hint"> · {t('mobile.phe.desktop_only', 'edit on desktop')}</span>
                </div>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-phe-phase">{t('mobile.phe.phase_lbl', 'Stage')}</label>
                <select id="m-phe-phase" className="m-sel" value={phase} onChange={(e) => setPhase(e.target.value as Phase)}>
                  {PHASE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.key, o.fallback)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-phe-op">{t('mobile.phe.operation_lbl', 'Operation')}</label>
                <select id="m-phe-op" className="m-sel" value={operation} onChange={(e) => setOperation(e.target.value as Operation)}>
                  {OPERATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.key, o.fallback)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-phe-action">{t('mobile.phe.action_lbl', 'Action')}</label>
                <select id="m-phe-action" className="m-sel" value={actionType} onChange={(e) => setActionType(e.target.value as ActionType)}>
                  {ACTION_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.key, o.fallback)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-phe-onerror">{t('mobile.phe.on_error_lbl', 'On error')}</label>
                <select id="m-phe-onerror" className="m-sel" value={onError} onChange={(e) => setOnError(e.target.value as OnError)}>
                  {ON_ERROR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.key, o.fallback)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-phe-priority">{t('mobile.phe.priority', 'Priority')}</label>
                <input
                  id="m-phe-priority"
                  className="m-inp"
                  type="number"
                  inputMode="numeric"
                  value={priorityText}
                  onChange={(e) => setPriorityText(e.target.value)}
                />
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.phe.active_hint', 'Hook fires on matching writes')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow">
                <label>{t('mobile.phe.conditions', 'Conditions')}</label>
                <div className="m-formrow-ro">
                  {conditionsSummary(t, hook.conditions)}
                  <span className="m-formrow-hint"> · {t('mobile.phe.desktop_only', 'edit on desktop')}</span>
                </div>
              </div>
              <div className="m-formrow">
                <label>{t('mobile.phe.action_config', 'Config')}</label>
                <div className="m-formrow-ro">
                  {actionConfigSummary(t, actionType, hook.action_config)}
                  <span className="m-formrow-hint"> · {t('mobile.phe.desktop_only', 'edit on desktop')}</span>
                </div>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
